import { useState, useMemo } from "react";
import { useEditor, EditorContent, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import clsx from "clsx";
import { useMutation } from "@tanstack/react-query";
import { PopupModal } from "../../components/Modal";
import { useSendEmail } from "../../helpers/hooks/use-send-email";
import { draftReply, type DraftReplyResult } from "../../helpers/api/crm";

export interface ComposeContext {
  /**
   * What this compose instance is doing. `new` = fresh thread, `reply` = append
   * to an existing Gmail thread (threadId required).
   */
  mode: "new" | "reply";
  /** Required when `mode === "reply"`. */
  threadId?: string;
  /** Used to pre-fill subject in reply mode ("Re: …"). */
  parentSubject?: string;
  /**
   * Preview of the email being replied to. Shown above the editor so the CS rep
   * has context without leaving the modal.
   */
  parentSnippet?: string;
  /** For informational header only — the actual send-to is resolved server-side. */
  customerDisplayName?: string;
  customerEmail?: string;
}

interface Props {
  customerId: string;
  context: ComposeContext;
  onClose: () => void;
}

// ─── Toolbar button ────────────────────────────────────────────────────────

function ToolbarButton({
  label,
  active,
  onClick,
  title,
}: {
  label: string;
  active?: boolean;
  onClick: () => void;
  title: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-pressed={active}
      className={clsx(
        "px-2 py-1 rounded text-xs font-medium border transition-colors",
        active
          ? "bg-blue-100 text-blue-700 border-blue-200"
          : "bg-surface-primary text-content-secondary border-border-secondary hover:bg-surface-secondary",
      )}
    >
      {label}
    </button>
  );
}

function Toolbar({ editor }: { editor: Editor | null }) {
  if (!editor) return null;
  return (
    <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-border-secondary bg-surface-secondary">
      <ToolbarButton
        label="B"
        title="Bold (⌘B)"
        active={editor.isActive("bold")}
        onClick={() => editor.chain().focus().toggleBold().run()}
      />
      <ToolbarButton
        label="I"
        title="Italic (⌘I)"
        active={editor.isActive("italic")}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      />
      <span className="w-px h-5 bg-border-secondary mx-0.5" />
      <ToolbarButton
        label="• List"
        title="Bulleted list"
        active={editor.isActive("bulletList")}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      />
      <ToolbarButton
        label="1. List"
        title="Numbered list"
        active={editor.isActive("orderedList")}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      />
      <span className="w-px h-5 bg-border-secondary mx-0.5" />
      <ToolbarButton
        label="Link"
        title="Insert / edit link"
        active={editor.isActive("link")}
        onClick={() => {
          const prev = editor.getAttributes("link").href ?? "";
          const href = window.prompt("URL", prev);
          if (href === null) return; // cancelled
          if (href === "") {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
          } else {
            editor
              .chain()
              .focus()
              .extendMarkRange("link")
              .setLink({ href, target: "_blank", rel: "noopener noreferrer" })
              .run();
          }
        }}
      />
    </div>
  );
}

// ─── Compose modal ─────────────────────────────────────────────────────────

export function ComposeEmailModal({ customerId, context, onClose }: Props) {
  const initialSubject = useMemo(() => {
    if (context.mode !== "reply") return "";
    const s = context.parentSubject?.trim() ?? "";
    if (!s) return "Re: ";
    return /^re\s*:/i.test(s) ? s : `Re: ${s}`;
  }, [context]);

  const [subject, setSubject] = useState(initialSubject);
  const [cc, setCc] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [draftInstructions, setDraftInstructions] = useState("");
  const [showDraftControls, setShowDraftControls] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({}),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          class: "text-blue-600 underline",
          rel: "noopener noreferrer",
          target: "_blank",
        },
      }),
    ],
    content: "",
    editorProps: {
      attributes: {
        class:
          "prose prose-sm max-w-none focus:outline-none min-h-[220px] px-4 py-3 text-sm text-content-primary",
      },
    },
  });

  const mutation = useSendEmail(customerId);
  const subjectIsReadOnly = context.mode === "reply";

  // Phase 4 Stage 5 — Draft-with-AI. Hits the unified-knowledge-RAG backed
  // /admin/customers/:id/emails/draft-reply endpoint and prefills the editor.
  const draft = useMutation<
    DraftReplyResult,
    unknown,
    { additionalInstructions?: string }
  >({
    mutationFn: (vars) =>
      draftReply(customerId, {
        threadId: context.mode === "reply" ? context.threadId : undefined,
        additionalInstructions: vars.additionalInstructions,
      }),
    onSuccess: (result) => {
      editor?.commands.setContent(result.draftHtml || "");
      if (!subjectIsReadOnly && result.subject) {
        setSubject(result.subject);
      }
      setError(null);
    },
    onError: (err: any) => {
      const msg =
        err?.response?.data?.message ?? err?.message ?? "Draft failed.";
      setError(`Draft failed — ${msg}`);
    },
  });

  const handleSend = () => {
    setError(null);
    const bodyHtml = editor?.getHTML() ?? "";
    const textContent = editor?.getText().trim() ?? "";

    if (!subject.trim()) {
      setError("Subject is required.");
      return;
    }
    if (textContent.length === 0) {
      setError("Message body is empty.");
      return;
    }
    if (context.mode === "reply" && !context.threadId) {
      setError("Reply mode needs a threadId.");
      return;
    }

    const ccList = cc
      .split(/[,;]/)
      .map((s) => s.trim())
      .filter(Boolean);

    mutation.mutate(
      {
        mode: context.mode,
        threadId: context.mode === "reply" ? context.threadId : undefined,
        subject: subject.trim(),
        bodyHtml,
        cc: ccList.length ? ccList : undefined,
      },
      {
        onSuccess: () => onClose(),
        onError: (err: any) => {
          const serverMsg =
            err?.response?.data?.message ?? err?.message ?? "Send failed.";
          setError(serverMsg);
        },
      },
    );
  };

  const isSending = mutation.isPending;

  return (
    <PopupModal
      onClose={isSending ? undefined : onClose}
      contentClassName="w-[720px] max-w-[92vw] max-h-[90vh] overflow-auto"
    >
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-base font-semibold text-content-primary">
            {context.mode === "reply" ? "Reply in thread" : "New email"}
          </h2>
          {(context.customerDisplayName || context.customerEmail) && (
            <p className="text-xs text-content-tertiary mt-0.5">
              To:{" "}
              <span className="text-content-secondary">
                {context.customerDisplayName ?? ""}
                {context.customerEmail && context.customerDisplayName
                  ? " · "
                  : ""}
                {context.customerEmail ?? ""}
              </span>
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={isSending}
          className="text-content-tertiary hover:text-content-primary disabled:opacity-50 disabled:cursor-not-allowed text-lg leading-none"
          aria-label="Close"
        >
          ×
        </button>
      </div>

      {/* Parent context (reply mode) */}
      {context.mode === "reply" && context.parentSnippet && (
        <div className="rounded-md border border-border-secondary bg-surface-secondary px-3 py-2">
          <div className="text-[10px] uppercase tracking-wide text-content-tertiary mb-1">
            Replying to
          </div>
          <div className="text-xs text-content-secondary line-clamp-3">
            {context.parentSnippet}
          </div>
        </div>
      )}

      {/* Subject */}
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-content-tertiary">
          Subject
        </label>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          readOnly={subjectIsReadOnly}
          className={clsx(
            "w-full px-3 py-2 text-sm rounded-md border bg-surface-primary text-content-primary focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400",
            subjectIsReadOnly
              ? "border-border-primary bg-surface-secondary cursor-not-allowed"
              : "border-border-secondary",
          )}
        />
      </div>

      {/* Cc */}
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-content-tertiary">
          Cc <span className="text-content-tertiary">(comma-separated)</span>
        </label>
        <input
          type="text"
          value={cc}
          onChange={(e) => setCc(e.target.value)}
          placeholder="success@autumnplatform.com"
          className="w-full px-3 py-2 text-sm rounded-md border border-border-secondary bg-surface-primary text-content-primary focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400 placeholder:text-content-tertiary"
        />
      </div>

      {/* Editor */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-content-tertiary">
            Message
          </label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowDraftControls((v) => !v)}
              className="text-[11px] text-content-tertiary hover:text-content-primary"
            >
              {showDraftControls ? "Hide AI instructions" : "Add AI instructions"}
            </button>
            <button
              type="button"
              disabled={draft.isPending || isSending}
              onClick={() =>
                draft.mutate({
                  additionalInstructions: draftInstructions.trim()
                    ? draftInstructions.trim()
                    : undefined,
                })
              }
              className="text-xs px-2.5 py-1 rounded-md bg-primary/10 text-primary font-medium hover:bg-primary/15 disabled:opacity-50"
              title="Drafts a reply using the Autumn handbook + CS playbook + customer context"
            >
              {draft.isPending
                ? "Drafting…"
                : draft.isSuccess
                  ? "Regenerate with AI"
                  : "Draft with AI"}
            </button>
          </div>
        </div>
        {showDraftControls && (
          <textarea
            value={draftInstructions}
            onChange={(e) => setDraftInstructions(e.target.value)}
            placeholder="Optional nudge for the draft — e.g. 'Mention that the weekend maintenance window starts Saturday 2 AM'"
            rows={2}
            className="w-full text-xs px-2.5 py-1.5 border border-border-secondary rounded-md bg-surface-primary focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400 resize-y"
          />
        )}
        <div className="border border-border-secondary rounded-md overflow-hidden">
          <Toolbar editor={editor} />
          <EditorContent editor={editor} />
        </div>
        {draft.isSuccess && draft.data && (
          <p className="text-[11px] text-content-tertiary">
            Drafted from ticket {draft.data.sourceTicketId.slice(0, 8)} in{" "}
            {Math.round(draft.data.latencyMs / 100) / 10}s — review before
            sending.
          </p>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onClose}
          disabled={isSending}
          className="px-3 py-1.5 text-sm font-medium text-content-secondary border border-border-secondary rounded-md hover:bg-surface-secondary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Discard
        </button>
        <button
          type="button"
          onClick={handleSend}
          disabled={isSending}
          className="px-4 py-1.5 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSending ? "Sending…" : "Send"}
        </button>
      </div>
    </PopupModal>
  );
}
